/**
 * Daily Deals (CB) - JavaScript
 * Version: 1.1.0
 * Handles date-based progressive reveal of daily deals
 * Uses native Intl API for accurate timezone handling
 */

(function() {
  'use strict';

  /**
   * Get current date/time in specified timezone
   * Uses the browser's Intl API for accurate timezone conversion including DST
   * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
   * @returns {Date} Current date in the specified timezone
   */
  function getCurrentTimeInTimezone(timezone) {
    try {
      const now = new Date();

      // Use Intl.DateTimeFormat to get components in the target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
        minute: '2-digit',
        second: '2-digit'
      });

      const parts = formatter.formatToParts(now);
      const dateParts = {};

      parts.forEach(part => {
        if (part.type !== 'literal') {
          dateParts[part.type] = part.value;
        }
      });

      // Create a date object from the timezone-adjusted components
      return new Date(
        parseInt(dateParts.year, 10),
        parseInt(dateParts.month, 10) - 1, // Month is 0-indexed
        parseInt(dateParts.day, 10),
        parseInt(dateParts.hour, 10),
        parseInt(dateParts.minute, 10),
        parseInt(dateParts.second, 10)
      );
    } catch (e) {
      console.error('Error getting time in timezone:', timezone, e);
      // Fallback to local time if timezone is invalid
      return new Date();
    }
  }

  /**
   * Parse date string in format: YYYY-MM-DD HH:MM:SS
   * Interprets the date as being in the store's timezone
   * @param {string} dateStr - Date string
   * @param {string} timezone - IANA timezone identifier
   * @returns {Date|null} Parsed date or null
   */
  function parseDateTime(dateStr, timezone) {
    if (!dateStr || dateStr.trim() === '') return null;

    try {
      // Expected format: YYYY-MM-DD HH:MM:SS
      const parts = dateStr.trim().split(' ');
      if (parts.length !== 2) return null;

      const dateParts = parts[0].split('-');
      const timeParts = parts[1].split(':');

      if (dateParts.length !== 3 || timeParts.length !== 3) return null;

      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // 0-indexed
      const day = parseInt(dateParts[2], 10);
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = parseInt(timeParts[2], 10);

      // Create a date object - this represents the date/time in the store's timezone
      return new Date(year, month, day, hours, minutes, seconds);
    } catch (e) {
      console.error('Error parsing date:', dateStr, e);
      return null;
    }
  }

  /**
   * Check if two dates are the same day
   * @param {Date} date1
   * @param {Date} date2
   * @returns {boolean}
   */
  function isSameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  /**
   * Determine deal state
   * @param {Date} currentTime - Current time in store timezone
   * @param {Date} startDate - Deal start date in store timezone
   * @returns {string} 'future', 'today', or 'active'
   */
  function getDealState(currentTime, startDate) {
    if (!startDate) return 'future';

    if (currentTime < startDate) {
      return 'future';
    } else if (isSameDay(currentTime, startDate)) {
      return 'today';
    } else {
      return 'active';
    }
  }

  /**
   * Update deal states based on current time
   * @param {HTMLElement} container - Deals container
   */
  function updateDealStates(container) {
    const timezone = container.dataset.storeTimezone || 'America/New_York';

    // Get current time in the store's timezone
    const currentTime = getCurrentTimeInTimezone(timezone);

    const dealBlocks = container.querySelectorAll('[data-deal-block]');

    dealBlocks.forEach(function(block) {
      const startDateStr = block.dataset.startDate;

      // Parse the start date - it's already in the store's timezone
      const startDate = parseDateTime(startDateStr, timezone);

      const state = getDealState(currentTime, startDate);
      block.setAttribute('data-state', state);

      // Update ARIA attributes for accessibility
      if (state === 'future') {
        block.setAttribute('aria-label', 'Coming soon deal');
      } else if (state === 'today') {
        block.setAttribute('aria-label', 'Today\'s featured deal');
      } else {
        block.setAttribute('aria-label', 'Active deal');
      }
    });
  }

  /**
   * Initialize daily deals section
   */
  function initDailyDeals() {
    const containers = document.querySelectorAll('[data-daily-deals-container]');

    containers.forEach(function(container) {
      // Initial state update
      updateDealStates(container);

      // Update states every 30 seconds to catch day changes
      setInterval(function() {
        updateDealStates(container);
      }, 30000);
    });
  }

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDailyDeals);
  } else {
    initDailyDeals();
  }

  /**
   * Re-initialize on Shopify theme editor events
   */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.target.querySelector('[data-daily-deals-container]')) {
        setTimeout(initDailyDeals, 100);
      }
    });

    document.addEventListener('shopify:section:reorder', function() {
      setTimeout(initDailyDeals, 100);
    });

    document.addEventListener('shopify:block:select', function(event) {
      const block = event.target;
      if (block.hasAttribute('data-deal-block')) {
        // Highlight selected block in editor
        block.style.transition = 'transform 0.3s ease';
        block.style.transform = 'translateY(-10px)';

        setTimeout(function() {
          block.style.transform = '';
        }, 1000);
      }
    });
  }

})();
